import { z } from "zod"

export const reportingInventoryPathSchema = z
  .string()
  .max(2048)
  .refine((path) => {
    if (!path.startsWith("/") || path.startsWith("//") || /[?#\\\s]/.test(path)) return false
    try {
      return new URL(path, "https://inventory.invalid").pathname === path
    } catch {
      return false
    }
  }, "Expected an exact canonical path without query, fragment or traversal")

const inventoryUrlSchema = z
  .string()
  .max(2300)
  .refine((value) => {
    try {
      const url = new URL(value)
      return (
        url.protocol === "https:" && !url.username && !url.password && !url.search && !url.hash && url.href === value
      )
    } catch {
      return false
    }
  }, "Expected an exact HTTPS inventory URL")

export const reportingSeoInventoryProductSchema = z
  .object({
    productKey: z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/),
    sourceKey: z.string().nullable(),
    seoProductId: z.string().uuid().nullable(),
    status: z.enum(["available", "missing"]),
    reason: z.enum(["NOT_BOUND", "OWNERSHIP_MISMATCH", "SOURCE_UNAVAILABLE", "INVALID_CATALOG", "ROW_CAP"]).nullable(),
    domain: z.string().nullable(),
    pages: z
      .array(
        z
          .object({
            url: inventoryUrlSchema,
            kind: z.enum(["home", "tool", "managed-leaf", "hub"]),
            pageId: z.string().uuid().nullable(),
            state: z.enum(["draft", "gated", "published", "pruned"]).nullable(),
            updatedAt: z.string().datetime({ offset: true }).nullable(),
          })
          .strict(),
      )
      .max(10_000),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.status === "available" && (!value.sourceKey || !value.seoProductId || !value.domain || value.reason))
      ctx.addIssue({ code: "custom", message: "Available inventory requires verified source identity" })
    if (
      value.status === "missing" &&
      (!value.reason || value.pages.length || value.domain !== null || value.seoProductId !== null)
    )
      ctx.addIssue({ code: "custom", message: "Missing inventory exposes no catalog data" })
    if (new Set(value.pages.map((page) => page.url)).size !== value.pages.length)
      ctx.addIssue({ code: "custom", message: "Ambiguous catalog URL" })
    for (const page of value.pages) {
      if (inventoryUrlSchema.safeParse(page.url).success && new URL(page.url).hostname !== value.domain)
        ctx.addIssue({ code: "custom", message: "Catalog URL differs from bound domain" })
      if (page.kind === "managed-leaf" && (!page.pageId || !page.state || !page.updatedAt))
        ctx.addIssue({ code: "custom", message: "Managed leaves require catalog identity and state" })
    }
  })

export const reportingSeoInventorySchema = z
  .object({
    schemaVersion: z.literal(1),
    collectedAt: z.string().datetime({ offset: true }),
    products: z.array(reportingSeoInventoryProductSchema).max(20),
  })
  .strict()
  .refine(
    (value) => new Set(value.products.map((product) => product.productKey)).size === value.products.length,
    "Duplicate inventory product",
  )

export type ReportingSeoInventory = z.infer<typeof reportingSeoInventorySchema>
export type ReportingSeoInventoryProduct = z.infer<typeof reportingSeoInventoryProductSchema>
